<template>
  <div data-focus-view>
    <div class="tw:!text-center tw:!mb-8">
      <div class="tw:!w-16 tw:!h-16 tw:!bg-green-100 tw:!rounded-full tw:!flex tw:!items-center tw:!justify-center tw:!mx-auto tw:!mb-4">
        <CheckCircle class="tw:!w-10 tw:!h-10 tw:!text-green-600" />
      </div>
      <h1 class="tw:!text-2xl tw:!font-bold tw:!text-gray-900 tw:!mb-2">Payment Successful!</h1>
      <p class="tw:!text-gray-500 tw:!font-mono">Order #{{ store.currentOrderNo }}</p>
    </div>

    <div v-if="store.loading.profiles" class="tw:!flex tw:!flex-col tw:!items-center tw:!justify-center tw:!py-10">
      <LoaderCircle class="tw:!w-10 tw:!h-10 tw:!text-yellow-400 tw:!animate-spin" />
      <p class="tw:!mt-3 tw:!text-gray-500 tw:!animate-pulse">Preparing your eSIM...</p>
    </div>

    <div v-else-if="store.profiles?.length === 0" class="tw:!text-center tw:!py-10 tw:!text-gray-500">
      <p class="tw:!mb-4">Processing your eSIM profiles...</p>
      <button
        class="tw:!bg-yellow-400 tw:!text-gray-900 tw:!font-bold tw:!py-3 tw:!px-8 tw:!rounded-full tw:!hover:bg-yellow-500 tw:!transition-colors tw:!cursor-pointer"
        @click="store.queryPackages()"
      >
        Refresh Status
      </button>
    </div>

    <div v-else class="tw:!flex tw:!flex-col tw:!gap-4">
      <div
        v-for="(profile, index) in store.profiles"
        :key="profile.iccid"
        class="tw:!bg-white tw:!border tw:!border-gray-200 tw:!rounded-2xl tw:!p-4 md:tw:!p-5 tw:!shadow-sm"
      >
        <div class="tw:!flex tw:!justify-between tw:!items-center tw:!mb-4 tw:!pb-3 tw:!border-b tw:!border-gray-100">
          <h2 class="tw:!text-base md:tw:!text-lg tw:!font-bold tw:!text-gray-900">{{ $t('profile.esimTitle', { index: index + 1 }) }}</h2>
          <span
            v-for="pkgName in profile.packageList"
            :key="pkgName.packageCode"
            class="tw:!bg-gray-100 tw:!text-gray-700 tw:!text-[11px] tw:!font-bold tw:!px-2.5 tw:!py-1 tw:!rounded-full"
          >
            {{ pkgName.packageCode }}
          </span>
        </div>

        <div class="tw:!grid tw:!grid-cols-1 sm:tw:!grid-cols-[170px_1fr] tw:!gap-5">
          <div class="tw:!flex tw:!flex-col tw:!items-center">
            <div class="tw:!w-full tw:!max-w-[150px] tw:!aspect-square tw:!bg-white tw:!p-2 tw:!border tw:!border-gray-200 tw:!rounded-xl">
              <img :src="profile.qrCodeUrl" alt="eSIM QR Code" class="tw:!w-full tw:!h-full tw:!object-contain" />
            </div>
            <div class="tw:!mt-3">
              <p class="tw:!text-xs tw:!text-gray-500">{{ $t('profile.scanToInstall') }}</p>
            </div>
          </div>

          <div class="tw:!flex tw:!flex-col tw:!gap-3">
            <div class="tw:!bg-gray-50 tw:!p-2.5 tw:!rounded-lg tw:!border tw:!border-gray-100">
              <label class="tw:!block tw:!text-[11px] tw:!text-gray-500 tw:!uppercase tw:!font-semibold tw:!mb-1">
                {{ $t('profile.field.activationCode') }}
              </label>
              <div class="tw:!flex tw:!justify-between tw:!items-center tw:!gap-2">
                <span class="tw:!font-mono tw:!text-xs md:tw:!text-sm tw:!text-gray-900 tw:!truncate">
                  {{ getLpaParts(profile.ac).code }}
                </span>
                <button class="tw:!shrink-0 tw:!p-1 tw:!text-gray-400 tw:!hover:text-gray-600" @click="copyToClipboard(getLpaParts(profile.ac).code)">
                  <Copy class="tw:!w-4 tw:!h-4" />
                </button>
              </div>
            </div>

            <div class="tw:!bg-gray-50 tw:!border tw:!border-gray-100 tw:!rounded-2xl tw:!p-3 md:tw:!p-4">
              <div class="tw:!space-y-2.5">
                <div class="tw:!grid tw:!grid-cols-[105px_1fr] md:tw:!grid-cols-[130px_1fr] tw:!gap-2">
                  <span class="tw:!text-xs md:tw:!text-sm tw:!font-semibold tw:!text-gray-400">Expires:</span>
                  <span class="tw:!text-xs md:tw:!text-sm tw:!font-medium tw:!text-gray-900 tw:!break-all">
                    {{ formatDate(profile.expiredTime) }}
                  </span>
                </div>

                <div class="tw:!grid tw:!grid-cols-[105px_1fr] md:tw:!grid-cols-[130px_1fr] tw:!gap-2">
                  <span class="tw:!text-xs md:tw:!text-sm tw:!font-semibold tw:!text-gray-400">ICCID:</span>
                  <span class="tw:!text-xs md:tw:!text-sm tw:!font-medium tw:!text-gray-900 tw:!break-all">
                    {{ profile.iccid || '--' }}
                  </span>
                </div>

                <div class="tw:!grid tw:!grid-cols-[105px_1fr] md:tw:!grid-cols-[130px_1fr] tw:!gap-2">
                  <span class="tw:!text-xs md:tw:!text-sm tw:!font-semibold tw:!text-gray-400">APN:</span>
                  <span class="tw:!text-xs md:tw:!text-sm tw:!font-medium tw:!text-gray-900 tw:!break-all">
                    {{ profile.apn || '--' }}
                  </span>
                </div>
              </div>
            </div>

            <div class="tw:!bg-[#eef7ff] tw:!border tw:!border-[#cfe8ff] tw:!rounded-[1rem] tw:!p-4 md:tw:!p-5 tw:!mx-auto">
              <h3 class="tw:!text-lg md:tw:!text-xl tw:!font-semibold tw:!text-gray-900 tw:!mb-3">
                How to Install Your eSIM
              </h3>

              <ul class="tw:!list-disc tw:!pl-5 tw:!space-y-2 tw:!text-[#0b5e96] tw:!text-xs md:tw:!text-sm">
                <li>Open your camera and scan the QR code.</li>
                <li>Follow the prompts to add the eSIM profile to your device.</li>
                <li>Once added, select the eSIM for data connectivity.</li>
                <li>If you encounter any issues, refer to the full installation guide.</li>
              </ul>

              <a
                href="https://buyesimdata.com/how-to-buy-and-install-esim-data/"
                target="_blank"
                rel="noopener noreferrer"
                class="tw:!mt-4 tw:!min-h-[2.25rem] tw:!w-full tw:!rounded-xl tw:!bg-[#1da1db] tw:!text-white tw:!text-sm md:tw:!text-base tw:!font-bold tw:!no-underline tw:!flex tw:!items-center tw:!justify-center tw:!px-4 tw:!text-center"
              >
                View Full Installation Guide
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="tw:!mt-8 tw:!flex tw:!justify-center">
        <button
          class="tw:!bg-yellow-400 tw:!text-gray-900 tw:!font-bold tw:!py-3 tw:!px-8 tw:!rounded-full tw:!border-none tw:!cursor-pointer tw:!hover:bg-yellow-500 tw:!transition-colors"
          @click="store.HomeStep()"
        >
          {{ $t('button.backToHome') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { CheckCircle, Copy, LoaderCircle } from 'lucide-vue-next'
import { formatDate, sleep } from '~/composables/general'
import { useStore } from '~/composables/widgetMode'

const store = useStore()
const isCopied = ref(false)

const getLpaParts = (ac: string) => {
  if (!ac) return { address: '', code: '' }
  const parts = ac.split('$')
  return {
    address: parts[1] || '',
    code: parts[2] || ''
  }
}

const copyToClipboard = async (text: string) => {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    isCopied.value = true
    setTimeout(() => {
      isCopied.value = false
    }, 1600)
  } catch (err) {
    console.error('Failed to copy', err)
  }
}

onMounted(async () => {
  if (store.currentOrderNo) {
    store.loading.profiles = true
    await sleep(6000)
    await store.queryPackages()
  }
})
</script>